import { model as i18n } from '!/i18n'
import { model as cartModel } from '$/cart'
import { model as paymentInfoModel } from '$/payment-info'
import { model as priceSettingsModel } from '$/price-settings'
import { api, type TvShowSeason } from '@setplex/tria-api'
import { createEvent, sample } from 'effector'
import { aye } from '~/shared/helpers'
import {
  filterSeasonPriceSettingsOnly,
  getPerItemPriceSettingsOnly,
  getPerSetPriceSettingsOnly,
  getTvShowSeasonPaymentTitle,
  isPayForBundleItemSeason,
  isPayForBundleItemTvShow,
  isPayForItemSeason,
  isPayForItemTvShow,
} from '../../lib'
import { anyPaymentInitiated } from './model'

export const selectTvShowSeasonPaymentButton = createEvent()

export const payForTvShowSeason = createEvent<TvShowSeason | null>()

sample({
  clock: selectTvShowSeasonPaymentButton,
  source: api.tvshows.$tvShowSeason,
  fn: (season) => season,
  target: payForTvShowSeason,
})

// TODO: fix types for season payment flow
sample({
  clock: payForTvShowSeason,
  source: api.tvshows.$tvShowSeason,
  filter: (season) => aye(season),
  fn: (season) => filterSeasonPriceSettingsOnly(season!),
  target: priceSettingsModel.$priceSettings,
})

// click on season payment button with seasonPriceSettings with PER_ITEM payment method
// 1. Set price settings
sample({
  clock: payForTvShowSeason,
  source: api.tvshows.$tvShowSeason,
  filter: (season) => {
    if (!season) return false

    const filteredSeasonPriceSettings = filterSeasonPriceSettingsOnly(season)

    return isPayForItemSeason({
      seasonPriceSettings: filteredSeasonPriceSettings,
      free: Boolean(season.free),
      purchaseInfo: season.purchaseInfo,
    })
  },
  fn: (season) => {
    const filteredSeasonPriceSettings = filterSeasonPriceSettingsOnly(season!)
    return getPerItemPriceSettingsOnly(filteredSeasonPriceSettings)
  },
  target: priceSettingsModel.$priceSettings,
})

sample({
  clock: payForTvShowSeason,
  target: anyPaymentInitiated,
})

// 2. Set ui payment info
sample({
  clock: payForTvShowSeason,
  source: {
    show: api.tvshows.$currentTvShow,
    season: api.tvshows.$tvShowSeason,
    episodes: api.tvshows.$tvShowSeasonEpisodes,
    i18n: i18n.$t,
  },
  filter: ({ season }) => {
    if (!season) return false

    const filteredSeasonPriceSettings = filterSeasonPriceSettingsOnly(season)
    const isPayForItem = isPayForItemSeason({
      seasonPriceSettings: filteredSeasonPriceSettings,
      free: Boolean(season.free),
      purchaseInfo: season?.purchaseInfo,
    })

    return isPayForItem && Boolean(filteredSeasonPriceSettings?.length)
  },
  fn: ({ show, season, episodes, i18n: { t } }) => {
    const { name: showName = '' } = show ?? {}
    const { year, landscapeImageUrl: seasonImageUrl } = season ?? {}
    const episodeQuantity = episodes.length

    const title = getTvShowSeasonPaymentTitle({
      seasonNumber: season?.displayNumber,
      t,
    })

    const totalEpisodesInSeasonString = `${episodeQuantity} Episodes`
    const labels = [totalEpisodesInSeasonString, String(year)].filter(Boolean)

    const filteredSeasonPriceSettings = filterSeasonPriceSettingsOnly(season!)

    return {
      name: showName,
      title,
      imageUrl: seasonImageUrl,
      labels,
      priceSettings: getPerItemPriceSettingsOnly(filteredSeasonPriceSettings),
    }
  },
  target: paymentInfoModel.$uiPaymentInfo,
})

// 3. Set payment info for cart
sample({
  clock: payForTvShowSeason,
  source: api.tvshows.$tvShowSeason,
  filter: (season) => {
    if (!season) return false

    const filteredSeasonPriceSettings = filterSeasonPriceSettingsOnly(season)
    const isPayForItem = isPayForItemSeason({
      seasonPriceSettings: filteredSeasonPriceSettings,
      free: Boolean(season.free),
      purchaseInfo: season?.purchaseInfo,
    })

    return isPayForItem && Boolean(filteredSeasonPriceSettings?.length)
  },
  fn: (season) => ({
    seasonId: season!.id,
  }),
  target: cartModel.$tvShowSeasonInfoForCart,
})

// click on season payment button with seasonPriceSettings with PER_SET payment method
// 1. Set price settings
sample({
  clock: payForTvShowSeason,
  source: api.tvshows.$tvShowSeason,
  filter: (season) => {
    if (!season) return false

    const filteredSeasonPriceSettings = filterSeasonPriceSettingsOnly(season)

    return (
      isPayForBundleItemSeason(season) &&
      Boolean(filteredSeasonPriceSettings?.length)
    )
  },
  fn: (season) => {
    const filteredSeasonPriceSettings = filterSeasonPriceSettingsOnly(season!)

    return getPerSetPriceSettingsOnly(filteredSeasonPriceSettings)
  },
  target: priceSettingsModel.$priceSettings,
})

// 2. Load bundle info (fill $priceSettings and $paymentInfoForCart in bundle file)
sample({
  clock: payForTvShowSeason,
  source: api.tvshows.$tvShowSeason,
  filter: (season) => aye(season) && isPayForBundleItemSeason(season!),
  fn: (season) => {
    const priceSettings = getPerSetPriceSettingsOnly(
      season!.seasonPriceSettings
    )

    return {
      bundleId: priceSettings?.[0].bundle?.id ?? 0,
    }
  },
  target: api.showBundles.loadShowBundleInfoForContent,
})

// click on season payment button with tvShowPriceSettings with PER_ITEM payment method
// 1. Set price settings
sample({
  clock: payForTvShowSeason,
  source: api.tvshows.$tvShowSeason,
  filter: (season) => {
    if (!season) return false
    const filteredSeasonPriceSettings = filterSeasonPriceSettingsOnly(season)

    return !filteredSeasonPriceSettings?.length && isPayForItemTvShow(season)
  },
  fn: (season) => {
    return getPerItemPriceSettingsOnly(season!.tvShowPriceSettings)
  },
  target: priceSettingsModel.$priceSettings,
})

// 2. Set ui payment info
sample({
  clock: payForTvShowSeason,
  source: {
    show: api.tvshows.$currentTvShow,
    season: api.tvshows.$tvShowSeason,
    i18n: i18n.$t,
  },
  filter: ({ season }) => {
    if (!season) return false
    const filteredSeasonPriceSettings = filterSeasonPriceSettingsOnly(season)

    return !filteredSeasonPriceSettings?.length && isPayForItemTvShow(season)
  },
  fn: ({ show, season, i18n: { t } }) => {
    const {
      ageRatings = '',
      year,
      name = '',
      landscapeImageUrl: showImageUrl,
    } = show ?? {}

    const labels = [ageRatings, String(year)].filter(Boolean)

    return {
      name,
      title: t('series'),
      imageUrl: showImageUrl,
      labels,
      priceSettings: getPerItemPriceSettingsOnly(season!.tvShowPriceSettings),
    }
  },
  target: paymentInfoModel.$uiPaymentInfo,
})

// 3. Set payment info for cart
sample({
  clock: payForTvShowSeason,
  source: api.tvshows.$tvShowSeason,
  filter: (season) => {
    if (!season) return false
    const filteredSeasonPriceSettings = filterSeasonPriceSettingsOnly(season)

    return !filteredSeasonPriceSettings?.length && isPayForItemTvShow(season)
  },
  fn: (season) => ({
    seasonId: season!.id,
  }),
  target: cartModel.$tvShowSeasonInfoForCart,
})

// click on season payment button with tvShowPriceSettings with PER_SET payment method
// 1. Set price settings
sample({
  clock: payForTvShowSeason,
  source: api.tvshows.$tvShowSeason,
  filter: (season) => {
    if (!season) return false
    const filteredSeasonPriceSettings = filterSeasonPriceSettingsOnly(season)

    return (
      !filteredSeasonPriceSettings?.length && isPayForBundleItemTvShow(season)
    )
  },
  fn: (season) => {
    return getPerSetPriceSettingsOnly(season!.tvShowPriceSettings)
  },
  target: priceSettingsModel.$priceSettings,
})

// 2. Load bundle info (fill $priceSettings and $paymentInfoForCart in bundle file)
sample({
  clock: payForTvShowSeason,
  source: api.tvshows.$tvShowSeason,
  filter: (season) => {
    if (!season) return false
    const filteredSeasonPriceSettings = filterSeasonPriceSettingsOnly(season)

    return (
      !filteredSeasonPriceSettings?.length && isPayForBundleItemTvShow(season)
    )
  },
  fn: (content) => {
    const priceSettings = getPerSetPriceSettingsOnly(
      content!.tvShowPriceSettings
    )

    return {
      bundleId: priceSettings?.[0].bundle?.id ?? 0,
    }
  },
  target: api.showBundles.loadShowBundleInfoForContent,
})
